import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import RowActionsMenu from "../UI/RowActionsMenu";
import EmptyState from "../UI/EmptyState";
import SearchBar from "../UI/SearchBar";
import EditMatchDialog from "./EditMatchDialog";
import DeleteMatchDialog from "./DeleteMatchDialog";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";

const MatchTable = ({ teamFilter, onClearTeamFilter, ...props }) => {
  const navigate = useNavigate();
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [search, setSearch] = useState("");

  const rows = props.matches.map((match) => ({
    id: match.id,
    homeTeam: match.homeTeam,
    awayTeam: match.awayTeam,
    startDate: match.startDate,
    hasBeenPlayed: match.hasBeenPlayed,
    homeScore: match.homeScore,
    awayScore: match.awayScore,
    matchType: match.matchType,
    groupType: match.groupType,
    knockoutType: match.knockoutType,
  }));

  const getMatchTypeDisplay = (row) => {
    if (row.matchType === "GROUP") {
      return `Group: ${row.groupType || "N/A"}`;
    } else if (row.matchType === "KNOCKOUT") {
      return `Knockout: ${row.knockoutType?.replace(/_/g, " ") || "N/A"}`;
    }
    return "League";
  };

  const matchesWithPermission = props.matchesWithPermission.map((m) => m.id);

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<SportsSoccerIcon />}
        message="No matches yet."
      />
    );
  }

  const q = search.toLowerCase();
  const filteredRows = rows
    .filter(
      (row) =>
        row.homeTeam.name.toLowerCase().includes(q) ||
        row.awayTeam.name.toLowerCase().includes(q)
    )
    .filter(
      (row) =>
        !teamFilter ||
        row.homeTeam.name === teamFilter ||
        row.awayTeam.name === teamFilter
    );

  const noResults = filteredRows.length === 0;

  return (
    <>
      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search by team name..."
      />
      {teamFilter && (
        <Chip
          label={`Filtered: ${teamFilter}`}
          onDelete={onClearTeamFilter}
          size="small"
          sx={{ mb: 1 }}
        />
      )}
      {noResults ? (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          No matches match &ldquo;{search}&rdquo;.
        </Typography>
      ) : (
      <TableContainer
        className="tableContainer"
        data-testid="matchTable"
        component={Paper}
        sx={{ overflowX: "auto" }}
      >
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              {matchesWithPermission.length > 0 && <TableCell sx={{ width: 48, p: 0.5 }} />}
              <TableCell>Date</TableCell>
              <TableCell>Home Team</TableCell>
              <TableCell align="center">Score</TableCell>
              <TableCell>Away Team</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Played?</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row) => (
              <TableRow
                data-testid={"matchTableRow" + row.id}
                hover
                key={row.id}
                sx={{ "&:last-child td, &:last-child th": { border: 0 }, cursor: "pointer" }}
                onClick={() => navigate(`/match/${row.id}`)}
              >
                {matchesWithPermission.length > 0 && (
                  <TableCell sx={{ width: 48, p: 0.5 }} onClick={(e) => e.stopPropagation()}>
                    {matchesWithPermission.includes(row.id) && (
                      <RowActionsMenu
                        onEdit={() => setEditRow(row)}
                        onDelete={() => setDeleteRow(row)}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell>{row.startDate}</TableCell>
                <TableCell>{row.homeTeam.name}</TableCell>
                <TableCell align="center">
                  {row.hasBeenPlayed ? `${row.homeScore} – ${row.awayScore}` : "vs"}
                </TableCell>
                <TableCell>{row.awayTeam.name}</TableCell>
                <TableCell>{getMatchTypeDisplay(row)}</TableCell>
                <TableCell>
                  <Checkbox checked={row.hasBeenPlayed} disabled />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      {editRow && (
        <EditMatchDialog
          open
          setOpen={(open) => !open && setEditRow(null)}
          match={editRow}
        />
      )}
      {deleteRow && (
        <DeleteMatchDialog
          open
          setOpen={(open) => !open && setDeleteRow(null)}
          match={deleteRow}
        />
      )}
    </>
  );
};

export default MatchTable;
